import { useState, useEffect } from 'react';
import Drawer from '~/components/Drawer';
import { inputStyles, labelStyles, requiredStyles } from './styles';
import { updateApplicationFinancialInfo } from '~/services/campaign';

interface Props {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    application?: unknown;
    campaignRequestId?: number;
    onUpdate?: (response: any) => void;
}

const IDENTITY_CHOICES = [
    { value: 1, label: 'Client' },
    { value: 2, label: 'Spouse' },
    { value: 3, label: 'Parent of minor client' },
    { value: 4, label: 'Legal Guardian' },
    { value: 5, label: 'POA' },
];

// Format a number as currency (e.g., 4000 -> "4,000.00")
const formatCurrency = (value: string | number): string => {
    if (value === '' || value === null || value === undefined) return '';
    const num =
        typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

// Parse a formatted currency string back to a number string (e.g., "4,000.00" -> "4000.00")
const parseCurrency = (value: string): string => {
    if (!value) return '';
    // Remove commas and keep only digits, decimal point, and minus sign
    return value.replace(/,/g, '');
};

const FinancialInfoDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    application,
    campaignRequestId,
    onUpdate,
}) => {
    const applicationData = application as {
        fin_num_in_household?: number;
        fin_is_single_parent_household?: boolean;
        fin_is_irs_dependent?: boolean;
        fin_monthly_wages?: number;
        fin_soc_sec_income?: number;
        fin_disability_income?: number;
        fin_other_income?: number;
        fin_tot_monthly_income?: number;
        fin_anl_household_income?: number;
        fin_has_hospital_costs?: boolean;
        fin_has_medication_costs?: boolean;
        fin_has_travel_costs?: boolean;
        fin_has_phys_therapy_costs?: boolean;
        fin_has_copay_costs?: boolean;
        fin_has_home_healthcare_costs?: boolean;
        fin_has_post_care_costs?: boolean;
        fin_has_living_donor_costs?: boolean;
        fin_has_donor_search_costs?: boolean;
        fin_has_med_equip_costs?: boolean;
        fin_has_home_modification_costs?: boolean;
        fin_has_transportation_costs?: boolean;
        fin_has_emergency_living_expenses?: boolean;
        fin_other_comments?: string;
        fin_certify_identity?: number;
        fin_signature?: string;
        fin_signature_date?: string;
    };

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        numInHousehold: applicationData?.fin_num_in_household || '',
        isSingleParent:
            applicationData?.fin_is_single_parent_household ??
            (null as boolean | null),
        isIrsDependent:
            applicationData?.fin_is_irs_dependent ?? (null as boolean | null),
        monthlyWages: applicationData?.fin_monthly_wages || '',
        socSecIncome: applicationData?.fin_soc_sec_income || '',
        disabilityIncome: applicationData?.fin_disability_income || '',
        otherIncome: applicationData?.fin_other_income || '',
        totalMonthlyIncome: applicationData?.fin_tot_monthly_income || '',
        annualHouseholdIncome: applicationData?.fin_anl_household_income || '',
        hasHospitalCosts: applicationData?.fin_has_hospital_costs || false,
        hasMedicationCosts: applicationData?.fin_has_medication_costs || false,
        hasTravelCosts: applicationData?.fin_has_travel_costs || false,
        hasPhysTherapyCosts:
            applicationData?.fin_has_phys_therapy_costs || false,
        hasCopay: applicationData?.fin_has_copay_costs || false,
        hasHomeHealthcare:
            applicationData?.fin_has_home_healthcare_costs || false,
        hasPostCare: applicationData?.fin_has_post_care_costs || false,
        hasLivingDonor: applicationData?.fin_has_living_donor_costs || false,
        hasDonorSearch: applicationData?.fin_has_donor_search_costs || false,
        hasMedEquip: applicationData?.fin_has_med_equip_costs || false,
        hasHomeModification:
            applicationData?.fin_has_home_modification_costs || false,
        hasTransportation:
            applicationData?.fin_has_transportation_costs || false,
        hasEmergencyLivingExpenses:
            applicationData?.fin_has_emergency_living_expenses || false,
        otherComments: applicationData?.fin_other_comments || '',
        certifyIdentity: applicationData?.fin_certify_identity || '',
        signature: applicationData?.fin_signature || '',
        signatureDate: applicationData?.fin_signature_date
            ? applicationData.fin_signature_date.split('T')[0]
            : new Date().toISOString().split('T')[0],
    });

    const handleChange = (
        field: string,
        value: string | number | boolean | null,
    ) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleCheckboxChange = (field: string, checked: boolean) => {
        setFormData((prev) => ({ ...prev, [field]: checked }));
    };

    // Track which currency field is focused (to show raw value while typing)
    const [focusedCurrencyField, setFocusedCurrencyField] = useState<
        string | null
    >(null);

    // Handle currency input change - allow free typing
    const handleCurrencyChange = (field: string, value: string) => {
        // Allow only digits and decimal point during input
        const cleaned = value.replace(/[^0-9.]/g, '');
        setFormData((prev) => ({ ...prev, [field]: cleaned }));
    };

    // Format currency on blur
    const handleCurrencyBlur = (field: string) => {
        setFocusedCurrencyField(null);
        const value = formData[field as keyof typeof formData];
        if (value !== '' && value !== null && value !== undefined) {
            // Parse and re-format to ensure clean number
            const num = parseFloat(value as string);
            if (!isNaN(num)) {
                setFormData((prev) => ({ ...prev, [field]: num.toFixed(2) }));
            }
        }
    };

    // Handle focus - show raw value
    const handleCurrencyFocus = (field: string) => {
        setFocusedCurrencyField(field);
    };

    // Get display value for currency field - formatted when not focused, raw when focused
    const getCurrencyDisplayValue = (field: string): string => {
        const value = formData[field as keyof typeof formData];
        if (value === '' || value === null || value === undefined) return '';
        // Show raw value when focused for easy editing
        if (focusedCurrencyField === field) {
            return value as string;
        }
        // Show formatted value when not focused
        return formatCurrency(value as string | number);
    };

    // Calculate total monthly income when individual income fields change
    useEffect(() => {
        const wages = Number(formData.monthlyWages) || 0;
        const socSec = Number(formData.socSecIncome) || 0;
        const disability = Number(formData.disabilityIncome) || 0;
        const other = Number(formData.otherIncome) || 0;

        const total = wages + socSec + disability + other;
        setFormData((prev) => ({
            ...prev,
            totalMonthlyIncome: total > 0 ? total : '',
            annualHouseholdIncome: total > 0 ? total * 12 : '',
        }));
    }, [
        formData.monthlyWages,
        formData.socSecIncome,
        formData.disabilityIncome,
        formData.otherIncome,
    ]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!campaignRequestId) {
            console.error(
                'Campaign Request ID is required to save financial data',
            );
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                fin_num_in_household: formData.numInHousehold
                    ? parseInt(formData.numInHousehold.toString())
                    : null,
                fin_is_single_parent_household: formData.isSingleParent,
                fin_is_irs_dependent: formData.isIrsDependent,
                fin_monthly_wages: formData.monthlyWages
                    ? parseFloat(formData.monthlyWages.toString())
                    : null,
                fin_soc_sec_income: formData.socSecIncome
                    ? parseFloat(formData.socSecIncome.toString())
                    : null,
                fin_disability_income: formData.disabilityIncome
                    ? parseFloat(formData.disabilityIncome.toString())
                    : null,
                fin_other_income: formData.otherIncome
                    ? parseFloat(formData.otherIncome.toString())
                    : null,
                fin_tot_monthly_income: formData.totalMonthlyIncome
                    ? parseFloat(formData.totalMonthlyIncome.toString())
                    : null,
                fin_anl_household_income: formData.annualHouseholdIncome
                    ? parseFloat(formData.annualHouseholdIncome.toString())
                    : null,
                fin_has_hospital_costs: formData.hasHospitalCosts,
                fin_has_medication_costs: formData.hasMedicationCosts,
                fin_has_travel_costs: formData.hasTravelCosts,
                fin_has_phys_therapy_costs: formData.hasPhysTherapyCosts,
                fin_has_copay_costs: formData.hasCopay,
                fin_has_home_healthcare_costs: formData.hasHomeHealthcare,
                fin_has_post_care_costs: formData.hasPostCare,
                fin_has_living_donor_costs: formData.hasLivingDonor,
                fin_has_donor_search_costs: formData.hasDonorSearch,
                fin_has_med_equip_costs: formData.hasMedEquip,
                fin_has_home_modification_costs: formData.hasHomeModification,
                fin_has_transportation_costs: formData.hasTransportation,
                fin_has_emergency_living_expenses:
                    formData.hasEmergencyLivingExpenses,
                fin_other_comments: formData.otherComments,
                fin_certify_identity: formData.certifyIdentity
                    ? parseInt(formData.certifyIdentity.toString())
                    : null,
                fin_signature: formData.signature,
                fin_signature_date: formData.signatureDate || null,
            };

            console.log('Saving financial data:', payload);

            const response = await updateApplicationFinancialInfo(
                campaignRequestId,
                payload,
            );

            if (onUpdate) {
                onUpdate(response);
            }

            setIsOpen(false);
        } catch (error) {
            console.error('Error saving financial data:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title='Financial Info'>
            <form onSubmit={handleSubmit}>
                <div className='space-y-4'>
                    {/* Household Information */}
                    <div>
                        <label
                            htmlFor='fin-num-household'
                            className={labelStyles}
                        >
                            Number in household
                            <span className={requiredStyles}>*</span>
                        </label>
                        <input
                            type='number'
                            id='fin-num-household'
                            className={`${inputStyles} w-full`}
                            value={formData.numInHousehold}
                            onChange={(e) =>
                                handleChange('numInHousehold', e.target.value)
                            }
                            min='1'
                        />
                    </div>

                    <div>
                        <label className={labelStyles}>
                            Is this a single parent household?
                            <span className={requiredStyles}>*</span>
                        </label>
                        <div className='mt-2 flex gap-4'>
                            <label className='flex items-center'>
                                <input
                                    type='radio'
                                    name='isSingleParent'
                                    checked={formData.isSingleParent === true}
                                    onChange={() =>
                                        handleChange('isSingleParent', true)
                                    }
                                    className='mr-2'
                                />
                                Yes
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='radio'
                                    name='isSingleParent'
                                    checked={formData.isSingleParent === false}
                                    onChange={() =>
                                        handleChange('isSingleParent', false)
                                    }
                                    className='mr-2'
                                />
                                No
                            </label>
                        </div>
                    </div>

                    <div>
                        <label className={labelStyles}>
                            Does a parent, spouse or other relative claim you as
                            a dependent on their tax return?
                            <span className={requiredStyles}>*</span>
                        </label>
                        <div className='mt-2 flex gap-4'>
                            <label className='flex items-center'>
                                <input
                                    type='radio'
                                    name='isIrsDependent'
                                    checked={formData.isIrsDependent === true}
                                    onChange={() =>
                                        handleChange('isIrsDependent', true)
                                    }
                                    className='mr-2'
                                />
                                Yes
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='radio'
                                    name='isIrsDependent'
                                    checked={formData.isIrsDependent === false}
                                    onChange={() =>
                                        handleChange('isIrsDependent', false)
                                    }
                                    className='mr-2'
                                />
                                No
                            </label>
                        </div>
                    </div>

                    {/* Income Fields */}
                    <div className='border-t pt-4'>
                        <h3 className='mb-3 text-[14px]/[18px] font-bold'>
                            Monthly Income
                        </h3>

                        <div className='space-y-3'>
                            <div>
                                <label
                                    htmlFor='fin-monthly-wages'
                                    className={labelStyles}
                                >
                                    Monthly Salary/Wages/Tips
                                </label>
                                <div className='flex items-center gap-2'>
                                    <span className='text-[16px]/[20px]'>
                                        $
                                    </span>
                                    <input
                                        type='text'
                                        id='fin-monthly-wages'
                                        className={`${inputStyles} w-full`}
                                        value={getCurrencyDisplayValue(
                                            'monthlyWages',
                                        )}
                                        onChange={(e) =>
                                            handleCurrencyChange(
                                                'monthlyWages',
                                                e.target.value,
                                            )
                                        }
                                        onFocus={() =>
                                            handleCurrencyFocus('monthlyWages')
                                        }
                                        onBlur={() =>
                                            handleCurrencyBlur('monthlyWages')
                                        }
                                        placeholder='0.00'
                                    />
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor='fin-soc-sec'
                                    className={labelStyles}
                                >
                                    Social Security
                                </label>
                                <div className='flex items-center gap-2'>
                                    <span className='text-[16px]/[20px]'>
                                        $
                                    </span>
                                    <input
                                        type='text'
                                        id='fin-soc-sec'
                                        className={`${inputStyles} w-full`}
                                        value={getCurrencyDisplayValue(
                                            'socSecIncome',
                                        )}
                                        onChange={(e) =>
                                            handleCurrencyChange(
                                                'socSecIncome',
                                                e.target.value,
                                            )
                                        }
                                        onFocus={() =>
                                            handleCurrencyFocus('socSecIncome')
                                        }
                                        onBlur={() =>
                                            handleCurrencyBlur('socSecIncome')
                                        }
                                        placeholder='0.00'
                                    />
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor='fin-disability'
                                    className={labelStyles}
                                >
                                    Social Security Disability
                                </label>
                                <div className='flex items-center gap-2'>
                                    <span className='text-[16px]/[20px]'>
                                        $
                                    </span>
                                    <input
                                        type='text'
                                        id='fin-disability'
                                        className={`${inputStyles} w-full`}
                                        value={getCurrencyDisplayValue(
                                            'disabilityIncome',
                                        )}
                                        onChange={(e) =>
                                            handleCurrencyChange(
                                                'disabilityIncome',
                                                e.target.value,
                                            )
                                        }
                                        onFocus={() =>
                                            handleCurrencyFocus(
                                                'disabilityIncome',
                                            )
                                        }
                                        onBlur={() =>
                                            handleCurrencyBlur(
                                                'disabilityIncome',
                                            )
                                        }
                                        placeholder='0.00'
                                    />
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor='fin-other-income'
                                    className={labelStyles}
                                >
                                    Other Income (such as capital gains,
                                    dividends, interest, rents, pensions,
                                    alimony)
                                </label>
                                <div className='flex items-center gap-2'>
                                    <span className='text-[16px]/[20px]'>
                                        $
                                    </span>
                                    <input
                                        type='text'
                                        id='fin-other-income'
                                        className={`${inputStyles} w-full`}
                                        value={getCurrencyDisplayValue(
                                            'otherIncome',
                                        )}
                                        onChange={(e) =>
                                            handleCurrencyChange(
                                                'otherIncome',
                                                e.target.value,
                                            )
                                        }
                                        onFocus={() =>
                                            handleCurrencyFocus('otherIncome')
                                        }
                                        onBlur={() =>
                                            handleCurrencyBlur('otherIncome')
                                        }
                                        placeholder='0.00'
                                    />
                                </div>
                            </div>

                            <div className='rounded-lg bg-[#F3F4F6] p-3'>
                                <div className='mb-2'>
                                    <label className='text-[12px]/[16px] font-bold text-[#6B7280]'>
                                        Total monthly income (sum of the above)
                                    </label>
                                    <div className='text-[16px]/[20px] font-bold'>
                                        $
                                        {formData.totalMonthlyIncome
                                            ? parseFloat(
                                                  formData.totalMonthlyIncome.toString(),
                                              ).toLocaleString('en-US', {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                              })
                                            : '0.00'}
                                    </div>
                                </div>
                                <div>
                                    <label className='text-[12px]/[16px] font-bold text-[#6B7280]'>
                                        Annual household income (monthly income
                                        x 12)
                                    </label>
                                    <div className='text-[16px]/[20px] font-bold'>
                                        $
                                        {formData.annualHouseholdIncome
                                            ? parseFloat(
                                                  formData.annualHouseholdIncome.toString(),
                                              ).toLocaleString('en-US', {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                              })
                                            : '0.00'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Anticipated Expenses */}
                    <div className='border-t pt-4'>
                        <label className={labelStyles}>
                            Please check all your anticipated out-of-pocket
                            medical and related expenses
                            <span className={requiredStyles}>*</span>
                        </label>
                        <div className='mt-2 space-y-2'>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasHospitalCosts}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasHospitalCosts',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Hospital
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasMedicationCosts}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasMedicationCosts',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Medication
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasTravelCosts}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasTravelCosts',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Travel
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasPhysTherapyCosts}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasPhysTherapyCosts',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Physical Therapy
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasCopay}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasCopay',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Co-pay
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasHomeHealthcare}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasHomeHealthcare',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Home Healthcare
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasPostCare}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasPostCare',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Post Care
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasLivingDonor}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasLivingDonor',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Living Donor
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasDonorSearch}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasDonorSearch',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Donor Search
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasMedEquip}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasMedEquip',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Medical Equipment
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasHomeModification}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasHomeModification',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Home Modification
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={formData.hasTransportation}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasTransportation',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Transportation
                            </label>
                            <label className='flex items-center'>
                                <input
                                    type='checkbox'
                                    checked={
                                        formData.hasEmergencyLivingExpenses
                                    }
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'hasEmergencyLivingExpenses',
                                            e.target.checked,
                                        )
                                    }
                                    className='mr-2'
                                />
                                Emergency Living Expenses
                            </label>
                        </div>
                    </div>

                    <div>
                        <label
                            htmlFor='fin-other-comments'
                            className={labelStyles}
                        >
                            Other/additional comments
                        </label>
                        <textarea
                            id='fin-other-comments'
                            className={`${inputStyles} w-full`}
                            value={formData.otherComments}
                            onChange={(e) =>
                                handleChange('otherComments', e.target.value)
                            }
                            rows={4}
                        />
                    </div>

                    {/* Certification */}
                    <div className='border-t pt-4'>
                        <h3 className='mb-3 text-[14px]/[18px] font-bold'>
                            Certification
                        </h3>

                        <div>
                            <label
                                htmlFor='fin-certify-identity'
                                className={labelStyles}
                            >
                                I certify that I am the:
                                <span className={requiredStyles}>*</span>
                            </label>
                            <select
                                id='fin-certify-identity'
                                className={`${inputStyles} w-full`}
                                value={formData.certifyIdentity}
                                onChange={(e) =>
                                    handleChange(
                                        'certifyIdentity',
                                        e.target.value,
                                    )
                                }
                            >
                                <option value=''>Select</option>
                                {IDENTITY_CHOICES.map((choice) => (
                                    <option
                                        key={choice.value}
                                        value={choice.value}
                                    >
                                        {choice.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label
                                htmlFor='fin-signature'
                                className={labelStyles}
                            >
                                Name<span className={requiredStyles}>*</span>
                            </label>
                            <input
                                type='text'
                                id='fin-signature'
                                className={`${inputStyles} w-full`}
                                value={formData.signature}
                                onChange={(e) =>
                                    handleChange('signature', e.target.value)
                                }
                            />
                        </div>

                        <div>
                            <label
                                htmlFor='fin-signature-date'
                                className={labelStyles}
                            >
                                Date<span className={requiredStyles}>*</span>
                            </label>
                            <input
                                type='date'
                                id='fin-signature-date'
                                className={`${inputStyles} w-full`}
                                value={formData.signatureDate}
                                onChange={(e) =>
                                    handleChange(
                                        'signatureDate',
                                        e.target.value,
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className='mb-[30px] flex gap-[16px]'>
                    <button
                        type='button'
                        className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        onClick={() => setIsOpen(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:cursor-not-allowed disabled:opacity-50'
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <span className='flex items-center justify-center'>
                                <svg
                                    className='mr-3 h-5 w-5 animate-spin'
                                    viewBox='0 0 24 24'
                                >
                                    <circle
                                        className='opacity-25'
                                        cx='12'
                                        cy='12'
                                        r='10'
                                        stroke='currentColor'
                                        strokeWidth='4'
                                    />
                                    <path
                                        className='opacity-75'
                                        fill='currentColor'
                                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                    />
                                </svg>
                                Saving...
                            </span>
                        ) : (
                            'Save'
                        )}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default FinancialInfoDrawer;
